
import React, { useEffect, useState } from 'react';
import { 
  loginWithGoogle, 
  logout, 
  subscribeToAuthChanges, 
  subscribeToTasks,
  addTask,
  updateTaskQuadrant,
  updateTaskStatus,
  deleteTask,
  saveUserFocusSettings,
  organizeTasksWithAI,
  updateTaskFocusTime,
  completeOnboarding
} from './services/firebaseService';
import { AppState, Quadrant, TaskStatus, FocusSettings, AIMagicResult } from './types';
import { Layout } from './components/Layout';
import { MatrixView } from './views/MatrixView';
import { KanbanView } from './views/KanbanView';
import { SettingsView } from './views/SettingsView';
import { FocusView } from './views/FocusView';
import { AnalyticsView } from './views/AnalyticsView';
import { PopupView } from './views/PopupView';
import { DailyView } from './views/DailyView';
import { WelcomeView } from './views/WelcomeView';
import { MagicSortModal } from './components/MagicSortModal';
import { WelcomeModal } from './components/WelcomeModal';
import { PricingModal } from './components/PricingModal';
import { Button } from './components/Button';
import { FREE_TIER_LIMIT } from './constants';
import { Loader2 } from 'lucide-react';

export const App: React.FC = () => {
  const [state, setState] = useState<AppState & { showPricingModal: boolean, pricingTrigger?: string }>({
    user: null,
    tasks: [],
    loading: true,
    view: 'matrix',
    showPricingModal: false
  });

  // Context Detection
  const urlParams = new URLSearchParams(window.location.search);
  const isExtension = window.location.protocol === 'chrome-extension:';
  const isPopup = isExtension && (urlParams.get('view') === 'popup' || window.innerHeight < 600);
  const isWelcome = urlParams.get('view') === 'welcome';
  const shouldAutoOpenPricing = urlParams.get('upgrade') === 'true';


  useEffect(() => {
    const unsubscribe = subscribeToAuthChanges((user) => {
      setState(prev => ({ ...prev, user, loading: false }));
    });
    return () => unsubscribe();
  }, []);

  // Theme Sync Logic
  useEffect(() => {
    const theme = state.user?.theme || 'light';
    const root = document.documentElement;
    root.classList.remove('dark', 'midnight');
    if (theme === 'dark') root.classList.add('dark');
    else if (theme === 'midnight') root.classList.add('dark', 'midnight');
  }, [state.user?.theme]);

  useEffect(() => {
    if (!state.user) {
      setState(prev => ({ ...prev, tasks: [] }));
      return;
    }
    const unsubscribe = subscribeToTasks(state.user.uid, (tasks) => {
      setState(prev => ({ ...prev, tasks }));
    });
    return () => unsubscribe();
  }, [state.user]);

  useEffect(() => {
    if (
      shouldAutoOpenPricing &&
      state.user &&
      !state.user.isPro
    ) {
      setState(prev => ({
        ...prev,
        showPricingModal: true,
        pricingTrigger: 'Upgrade from extension',
      }));

      // limpa a URL para evitar abrir de novo ao recarregar
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
    }
  }, [shouldAutoOpenPricing, state.user]);


  const handleLogin = async () => {
    try {
      await loginWithGoogle();
    } catch (error) {
      console.error("Login failed:", error);
    }
  };

  const handleOpenPricing = (reason?: string) => {
    setState(prev => ({ ...prev, showPricingModal: true, pricingTrigger: reason }));
  };

  const handleAddTask = async (title: string, quadrant: Quadrant, isRoutine: boolean = false) => {
    if (!state.user) return;
    if (!state.user.isPro && state.tasks.length >= FREE_TIER_LIMIT) {
      handleOpenPricing(`You've reached the ${FREE_TIER_LIMIT} task limit.`);
      return;
    }
    await addTask(state.user.uid, title, quadrant, isRoutine);
  };

  // Fix: Added handleUpdateSettings to satisfy the prop requirement in SettingsView
  const handleUpdateSettings = async (settings: FocusSettings) => {
    if (!state.user) return;
    await saveUserFocusSettings(state.user.uid, settings);
  };

  if (state.loading) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-gray-50 dark:bg-dark-bg">
        <Loader2 className="animate-spin text-blue-600" size={48} />
      </div>
    );
  }

  if (!state.user && isWelcome) return <WelcomeView />;

  if (!state.user) {
    return (
      <div className="h-screen w-full flex flex-col items-center justify-center bg-gray-50 dark:bg-dark-bg p-4 text-center">
        <img src="logo.png" className="w-16 h-16 rounded-2xl mx-auto mb-6 shadow-xl object-cover" alt="Logo" />
        <h1 className="text-2xl font-bold mb-2">Eisenhower Pro</h1>
        <Button onClick={handleLogin} className="w-full max-w-xs mt-6 h-12">
          Sign in with Google
        </Button>
      </div>
    );
  }

  // Render optimized for Extension Popup
  if (isPopup) {
    return (
      <PopupView 
        user={state.user} 
        tasks={state.tasks} 
        onAddTask={handleAddTask} 
        onUpdateStatus={updateTaskStatus} 
      />
    );
  }

  return (
    <Layout 
      user={state.user} 
      currentView={state.view} 
      onChangeView={(view) => setState(prev => ({ ...prev, view }))}
      onLogout={logout}
    >
      {state.view === 'matrix' && (
        <MatrixView 
          tasks={state.tasks}
          isPro={state.user.isPro}
          onAddTask={handleAddTask}
          onMoveTask={updateTaskQuadrant}
          onDeleteTask={deleteTask}
          onFocusTask={(id) => setState(prev => ({ ...prev, view: 'focus', activeTaskId: id }))}
          onOpenMagicSort={() => setState(prev => ({ ...prev, showMagicSort: true }))}
        />
      )}
      
      {state.view === 'kanban' && (
        <KanbanView tasks={state.tasks} onUpdateStatus={updateTaskStatus} onDeleteTask={deleteTask} onFocusTask={(id) => setState(prev => ({ ...prev, view: 'focus', activeTaskId: id }))} />
      )}

      {state.view === 'daily' && (
        <DailyView tasks={state.tasks} onAddTask={handleAddTask} onUpdateStatus={updateTaskStatus} onDeleteTask={deleteTask} isPro={state.user.isPro} onUpgrade={() => handleOpenPricing()} />
      )}

      {state.view === 'analytics' && <AnalyticsView tasks={state.tasks} isPro={state.user.isPro} />}

      {state.view === 'settings' && (
        <SettingsView 
          user={state.user}
          taskCount={state.tasks.length}
          onUpdateSettings={handleUpdateSettings}
          onUpgrade={() => handleOpenPricing()}
          onPricingTrigger={() => handleOpenPricing("Deep Focus Blocking is a Pro feature.")}
        />
      )}

      {state.view === 'focus' && state.activeTaskId && (
        <FocusView 
          task={state.tasks.find(t => t.id === state.activeTaskId)!}
          user={state.user}
          onComplete={async (id) => { await updateTaskStatus(id, TaskStatus.Done); setState(prev => ({ ...prev, view: 'matrix', activeTaskId: undefined })); }}
          onExit={() => setState(prev => ({ ...prev, view: 'matrix', activeTaskId: undefined }))}
          onLogTime={updateTaskFocusTime}
        />
      )}

      <MagicSortModal 
        isOpen={!!state.showMagicSort} 
        onClose={() => setState(prev => ({ ...prev, showMagicSort: false }))}
        onConfirm={async (magicTasks) => {
          for (const task of magicTasks) {
            if (!state.user?.isPro && state.tasks.length >= FREE_TIER_LIMIT) break;
            await addTask(state.user!.uid, task.title, task.quadrant);
          }
          setState(prev => ({ ...prev, showMagicSort: false }));
        }}
        analyzeFunction={organizeTasksWithAI}
        isPro={state.user.isPro}
      />
      
      <WelcomeModal isOpen={!!state.user.showWelcomeModal} onClose={async () => {
        setState(prev => ({ ...prev, user: prev.user ? { ...prev.user, showWelcomeModal: false } : null }));
        await completeOnboarding(state.user!.uid);
      }} />
      
      <PricingModal 
        isOpen={state.showPricingModal} 
        onClose={() => setState(prev => ({ ...prev, showPricingModal: false, pricingTrigger: undefined }))} 
        userEmail={state.user.email} 
        userId={state.user.uid} 
        triggerReason={state.pricingTrigger} 
      />
    </Layout>
  );
};
